import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRotateRight,
  faCodeBranch,
  faPenToSquare,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { SkeletonPage } from '@/components/skeletons';
import { toast, swalConfirm } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import { useReportTemplateVersions } from './hooks/useReportTemplates';
import { useCreateReportTemplateRevision } from './hooks/useReportTemplateMutations';
import {
  ReportTemplateDefaultBadge,
  ReportTemplateStatusBadge,
  ReportTemplateSystemBadge,
} from './components/ReportTemplateStatusBadge';

function extractMsg(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function statusOf(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

/**
 * Version history for one template family (B6.9e §5, over the B6.9d API).
 *
 * The whole chain comes from the server, from ANY member of the family — the
 * page never walks `previousVersionId` itself, never computes the next version
 * number, and never decides which version is the default. The backend is
 * authoritative for all of it (D-078).
 *
 * "Create New Version" is offered against the family's current PUBLISHED head,
 * and only when the family has no open draft: one open draft per family is the
 * rule the server enforces with a partial unique index, so offering a second
 * would be offering a guaranteed 409. When a draft does exist, the page points
 * at it instead — the answer to "let me revise this" is almost always "you
 * already are".
 */
export function ReportTemplateVersionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const can = useHasPermission();

  const query = useReportTemplateVersions(id);
  const revisionMutation = useCreateReportTemplateRevision();

  const back = (
    <button
      type="button"
      onClick={() => navigate(ROUTES.REPORT_TEMPLATES)}
      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      data-testid="versions-back"
    >
      <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
      Back to Report Templates
    </button>
  );

  if (query.isLoading) return <SkeletonPage />;

  if (query.error) {
    const status = statusOf(query.error);
    return (
      <div className="space-y-4" data-testid="versions-error">
        {back}
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mb-3 h-8 w-8 text-muted-foreground/40"
          />
          <p className="text-base font-medium text-foreground">
            {status === 403
              ? 'You do not have permission to view this version history.'
              : status === 404
                ? 'This report template no longer exists.'
                : 'Could not load the version history.'}
          </p>
          {status !== 403 && status !== 404 && (
            <Button
              variant="secondary"
              onClick={() => void query.refetch()}
              className="mt-4"
              data-testid="retry-versions"
            >
              <FontAwesomeIcon icon={faArrowRotateRight} className="mr-2 h-3.5 w-3.5" />
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  const history = query.data;
  if (!history) return <SkeletonPage />;

  /** The family's newest PUBLISHED version — the only legal revision source. */
  const publishedHead = history.versions.find((v) => v.status === 'PUBLISHED') ?? null;
  const openDraft = history.versions.find((v) => v.id === history.openDraftTemplateId) ?? null;
  const isSystemFamily = history.versions.some((v) => v.isSystem);

  /** Resolves a `previousVersionId` against the server's own list. */
  const parentOf = (previousVersionId: string) =>
    history.versions.find((v) => v.id === previousVersionId) ?? null;

  const handleNewVersion = async () => {
    if (!publishedHead) return;
    const ok = await swalConfirm({
      title: 'Create New Version?',
      html: `<p class="text-sm">A new draft (v${publishedHead.versionNumber + 1}) will be created from the published v${publishedHead.versionNumber}. That published version is left untouched.</p>`,
    });
    if (!ok) return;
    try {
      const created = await revisionMutation.mutateAsync(publishedHead.id);
      toast.success(`Draft v${created.versionNumber} created`);
      navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', created.id));
    } catch (e) {
      toast.error(extractMsg(e, 'Failed to create a new version'));
    }
  };

  return (
    <div className="space-y-6" data-testid="report-template-versions">
      {back}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Version history
          </p>
          <h1 className="truncate text-xl font-semibold text-foreground">
            {history.versions[0]?.name ?? history.slug}
          </h1>
          <p className="mt-1 font-mono text-[11px] text-muted-foreground" data-testid="family-slug">
            {history.slug}
          </p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="version-summary">
            {history.totalVersions} version{history.totalVersions === 1 ? '' : 's'} ·{' '}
            {history.publishedVersionCount} published
            {history.openDraftTemplateId ? ' · 1 open draft' : ' · no open draft'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {openDraft && (
            <Button
              variant="secondary"
              onClick={() => navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', openDraft.id))}
              data-testid="resume-draft"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="mr-2 h-3.5 w-3.5" />
              Resume draft v{openDraft.versionNumber}
            </Button>
          )}
          {!openDraft && publishedHead && !isSystemFamily && can('report-template:create') && (
            <Button
              onClick={() => void handleNewVersion()}
              disabled={revisionMutation.isPending}
              data-testid="create-new-version"
            >
              <FontAwesomeIcon icon={faCodeBranch} className="mr-2 h-3.5 w-3.5" />
              {revisionMutation.isPending ? 'Creating…' : 'Create New Version'}
            </Button>
          )}
        </div>
      </div>

      {isSystemFamily && (
        <p
          className="rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
          data-testid="versions-system-notice"
        >
          This is the platform-seeded system template. It is never versioned in place — clone it to
          start a template family of your own.
        </p>
      )}

      {/* The chain, newest first, exactly as the server ordered it. */}
      <ol className="space-y-2" data-testid="version-list">
        {history.versions.map((version) => {
          const isAnchor = version.id === history.requestedTemplateId;
          return (
            <li
              key={version.id}
              className={cn(
                'rounded-xl border bg-card px-4 py-3',
                isAnchor ? 'border-primary/50' : 'border-border',
              )}
              data-testid={`version-${version.versionNumber}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      v{version.versionNumber}
                    </span>
                    <ReportTemplateStatusBadge status={version.status} />
                    {version.isDefault && <ReportTemplateDefaultBadge />}
                    {version.isSystem && <ReportTemplateSystemBadge />}
                    {isAnchor && (
                      <span
                        className="rounded border border-primary/40 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                        data-testid="version-current-marker"
                      >
                        You are here
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Created {fmtDateTime(version.createdAt)}
                    {version.publishedAt && ` · Published ${fmtDateTime(version.publishedAt)}`}
                  </p>
                  {/* The parent is RESOLVED from the server's own chain by id,
                      never inferred as `versionNumber - 1`. A discarded draft
                      leaves a real gap (v1 → v3), so arithmetic would name a
                      version that does not exist. When the parent is absent from
                      the list — it was soft-deleted, and the history omits
                      soft-deleted rows — that is stated rather than guessed. */}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {version.previousVersionId === null
                      ? 'First version of this family'
                      : parentOf(version.previousVersionId)
                        ? `Derived from v${parentOf(version.previousVersionId)!.versionNumber}`
                        : 'Derived from an earlier version that has since been removed'}
                  </p>
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(ROUTES.REPORT_TEMPLATE_DESIGNER.replace(':id', version.id))
                  }
                  data-testid={`open-version-${version.versionNumber}`}
                >
                  {version.status === 'DRAFT' ? 'Open designer' : 'View design'}
                </Button>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
